import { useEffect, useState } from "react";

const BACKEND_URL = "";

// ─── Cyber-Tech palette (matches /fwa, /fwa-qr, CEOField) ─────────
const BG = "#020A1F";
const CYAN = "#00FFFF";
const WHITE = "#FFFFFF";
const SILVER = "#A0A0A0";
const MUTED = "#7a8ba1";
const RED = "#ff8a8a";
const GREEN = "#00DC78";
const MONO = "'JetBrains Mono', 'SF Mono', Menlo, monospace";
const SANS = "'Inter', -apple-system, sans-serif";

const injectFonts = () => {
  const ID = "vapp-fonts";
  if (document.getElementById(ID)) return;
  const l = document.createElement("link");
  l.id = ID; l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@600;700&family=Inter:wght@300;400;500;600;700;800&display=swap";
  document.head.appendChild(l);
};

const TRADES = [
  { group: "Tier A — Direct Crew (paid by Moovin Forward)", items: [
    { v: "cleaning", l: "Cleaning / Turnover Cleaning" },
    { v: "trash_out", l: "Trash-Out / Debris Removal" },
    { v: "punch_out", l: "Punch-Out / Cosmetic" },
    { v: "modular_install", l: "Modular Installer" },
    { v: "cabinet_reface", l: "Cabinet Refacing" },
    { v: "paint_touch_up", l: "Paint Touch-Up" },
    { v: "carpet_install", l: "Carpet Install" },
    { v: "lock_change", l: "Lock Change" },
    { v: "ada_504_fitout", l: "ADA / Section 504 Accessibility Fit-Out (grab bars, lever hardware, roll-in shower, offset commode)" },
  ]},
  { group: "Tier B — Referral Only (owner contracts direct)", items: [
    { v: "hvac", l: "HVAC" },
    { v: "electrical", l: "Electrical" },
    { v: "plumbing", l: "Plumbing" },
    { v: "pool", l: "Pool Service (CPO)" },
    { v: "lead_paint", l: "Lead Paint (EPA RRP)" },
    { v: "mold_remediation", l: "Mold Treatment" },
    { v: "asbestos", l: "Asbestos" },
    { v: "structural_roofing", l: "Structural Roofing" },
    { v: "gutter", l: "Gutter Services" },
  ]},
];

const DISCLAIMER_VERSION = "v2026.07.01";

const initial = {
  worker_type: "",  // "individual" | "business"
  business_name: "", contact_name: "", email: "", phone: "",
  trade: "", trades: [], zip_code: "", website: "",
  business_age_years: "", employee_count: "",
  owner_is_operator: null, is_franchise: false,
  has_public_parent: false, license_held_individually: null,
  black_owned: false, woman_owned: false, veteran_owned: false, lgbtq_owned: false,
  osha_30_attested: false,
  disclaimer_accepted: false,
  disclaimer_version: DISCLAIMER_VERSION,
  referred_by: "", notes: "",
};

const VendorApplication = () => {
  const STORAGE_KEY = "mf_vendor_app_draft";
  const [form, setForm] = useState(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) return { ...initial, ...JSON.parse(saved) };
      } catch { /* ignore */ }
    }
    return initial;
  });
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [savedNotice, setSavedNotice] = useState(false);

  useEffect(() => { injectFonts(); }, []);

  const set = (k, v) => setForm((prev) => {
    const next = { ...prev, [k]: v };
    if (typeof window !== "undefined") {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
    }
    return next;
  });

  const saveAndFinishLater = () => {
    if (typeof window !== "undefined") {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(form)); } catch { /* ignore */ }
    }
    setSavedNotice(true);
    setTimeout(() => setSavedNotice(false), 3500);
  };

  const isIndividual = form.worker_type === "individual";

  // If worker is an individual, business_name is auto-derived from contact_name
  // so the backend requirement is met without asking them for a company they don't have.
  const effectiveBusinessName = () =>
    isIndividual ? (form.contact_name.trim() || "Individual 1099") : form.business_name.trim();

  const canAdvance = () => {
    if (step === 1) {
      if (!form.worker_type) return false;
      if (!form.contact_name || !form.email) return false;
      if (!isIndividual && !form.business_name) return false;
      if (form.trades.length === 0) return false;
      return true;
    }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.contact_name || !form.email || form.trades.length === 0) {
      alert("Please fill in your name, email, and select at least one trade.");
      return;
    }
    if (!form.disclaimer_accepted) {
      alert("Please read and acknowledge the Contractor Terms & Disclaimer before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        business_name: effectiveBusinessName(),
        trade: form.trades[0] || "",
      };
      const r = await fetch(`${BACKEND_URL}/api/vendor-application`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (r.ok) {
        setResult(d);
        if (typeof window !== "undefined") {
          try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
        }
      } else {
        alert(d.detail || "Submission failed. Please try again or email hello@moovinforward.org.");
      }
    } catch (err) {
      alert("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Success screen
  if (result) {
    return (
      <div data-testid="vendor-app-result" style={pageStyle}>
        <div style={{ maxWidth: 640, margin: "3rem auto", background: "rgba(2,10,31,0.75)",
                      border: `1px solid ${SILVER}55`, padding: "2.4rem 2rem", borderRadius: 10,
                      boxShadow: `0 30px 80px rgba(0,0,0,0.55), 0 0 30px ${CYAN}33` }}>
          <div style={{ fontSize: "0.7rem", letterSpacing: "0.22em", textTransform: "uppercase",
                        color: CYAN, fontFamily: MONO, fontWeight: 800 }}>
            &gt; APPLICATION_RECEIVED
          </div>
          <h1 style={{ color: WHITE, fontSize: "1.9rem", marginTop: "0.4rem", letterSpacing: "-0.01em" }}>
            Thanks, {form.contact_name.split(" ")[0]}.
          </h1>
          <p style={{ color: SILVER, fontSize: "1rem", lineHeight: 1.6, marginTop: "1rem" }}>
            {result.message}
          </p>
          <div style={{ marginTop: "1.5rem", padding: "0.9rem 1.1rem",
                        background: `${CYAN}12`, border: `1px solid ${CYAN}44`,
                        borderRadius: 6, fontSize: "0.85rem", color: WHITE, fontFamily: MONO }}>
            <strong style={{ color: CYAN }}>Application ID:</strong> {result.application_id}<br />
            <strong style={{ color: CYAN }}>Tier:</strong> {result.tier === "A" ? "A — Direct Crew" : result.tier === "B" ? "B — Referral Only" : "Under review"}
          </div>
          <a href="/" data-testid="back-home" style={{ display: "inline-block", marginTop: "1.5rem",
             color: CYAN, fontFamily: MONO, fontWeight: 700, textDecoration: "underline", letterSpacing: "0.08em", fontSize: "0.8rem" }}>
            ← BACK_TO_MOOVIN.FORWARD
          </a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="vendor-application-page" style={pageStyle}>
      <div style={{ maxWidth: 720, margin: "2rem auto", padding: "0 1rem" }}>
        {/* Header */}
        <div style={{ borderTop: `2px solid ${CYAN}`, paddingTop: "1.2rem", marginBottom: "1.4rem" }}>
          <div style={{ fontSize: "0.7rem", letterSpacing: "0.22em", textTransform: "uppercase",
                        color: CYAN, fontFamily: MONO, fontWeight: 800 }}>
            &gt; MOOVIN FORWARD LLC · DFW
          </div>
          <h1 style={{ color: WHITE, fontSize: "2rem", margin: "0.4rem 0 0.6rem", letterSpacing: "-0.02em", lineHeight: 1.08 }} data-testid="vapp-headline">
            Contractor <span style={{ color: CYAN }}>&amp;</span> 1099 Worker <span style={{ color: CYAN }}>Application</span>
          </h1>
          <p style={{ color: SILVER, fontSize: "0.95rem", lineHeight: 1.6 }}>
            We&rsquo;re building a DFW network of independent workers &mdash; <strong style={{ color: WHITE }}>individual 1099 workers, sole proprietors, small crews, and growing LLCs</strong> looking to plug into steady work with property owners, receivers, and investor portfolios. <strong style={{ color: CYAN }}>Zero fee to join.</strong> If we bring you a project, you contract directly with the property owner &mdash; we&rsquo;re just the bridge.
          </p>
        </div>

        <form onSubmit={submit} style={{ background: "rgba(2,10,31,0.75)", border: `1px solid ${SILVER}55`,
              padding: "1.6rem 1.4rem", borderRadius: 8, boxShadow: `0 10px 40px rgba(0,0,0,0.35)` }}>

          {/* Wizard progress bar */}
          <div data-testid="wizard-progress" style={{ marginBottom: "1.4rem" }}>
            <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
              {[1, 2, 3].map((n) => (
                <div key={n} style={{
                  flex: 1, height: 6, borderRadius: 3,
                  background: step >= n ? CYAN : `${SILVER}33`,
                  boxShadow: step >= n ? `0 0 8px ${CYAN}66` : "none",
                  transition: "background 0.3s",
                }} />
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", color: MUTED, fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", fontFamily: MONO }}>
              <span style={{ color: step >= 1 ? CYAN : MUTED }}>Step 1 · Basics</span>
              <span style={{ color: step >= 2 ? CYAN : MUTED }}>Step 2 · About You</span>
              <span style={{ color: step >= 3 ? CYAN : MUTED }}>Step 3 · Safety + Notes</span>
            </div>
          </div>

          {step === 1 && <>
          {/* Worker type toggle */}
          <Section title="Who Are You?">
            <div style={{ color: SILVER, fontSize: "0.86rem", lineHeight: 1.55, marginBottom: "0.7rem" }}>
              Pick one. You do <strong style={{ color: WHITE }}>not</strong> need to have a registered business to work with us.
            </div>
            <div data-testid="worker-type-toggle" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <WorkerTypeButton
                testid="worker-type-individual"
                active={form.worker_type === "individual"}
                onClick={() => set("worker_type", "individual")}
                label="Individual / 1099 Worker"
                hint="No LLC needed. You do the work yourself or with a small crew you organize."
              />
              <WorkerTypeButton
                testid="worker-type-business"
                active={form.worker_type === "business"}
                onClick={() => set("worker_type", "business")}
                label="Business / LLC / Sole Proprietor"
                hint="You operate as a registered business, sole proprietor, or LLC."
              />
            </div>
          </Section>

          {form.worker_type && (
            <Section title={isIndividual ? "Your Info" : "Your Business"}>
              {!isIndividual && (
                <Field label="Business Name *" testid="business_name" value={form.business_name} onChange={(v) => set("business_name", v)} />
              )}
              <Field label={isIndividual ? "Your Full Name *" : "Your Name *"} testid="contact_name" value={form.contact_name} onChange={(v) => set("contact_name", v)} />
              <Row>
                <Field label="Email *" type="email" testid="email" value={form.email} onChange={(v) => set("email", v)} />
                <Field label="Phone" testid="phone" value={form.phone} onChange={(v) => set("phone", v)} placeholder="(817) 555-0000" />
              </Row>
              <Row>
                <Field label="ZIP Code" testid="zip_code" value={form.zip_code} onChange={(v) => set("zip_code", v)} placeholder="76102" />
                <Field label={isIndividual ? "Website / Social (optional)" : "Website (optional)"} testid="website" value={form.website} onChange={(v) => set("website", v)} />
              </Row>
            </Section>
          )}

          {form.worker_type && (
          <Section title="Trade / Service">
            <label style={labelStyle}>Which trade(s) do you offer? * <span style={{ color: CYAN, fontWeight: 600, fontFamily: MONO, fontSize: "0.75rem" }}>(check all that apply — e.g., Cleaning + Trash-Out)</span></label>
            <div data-testid="trades-multiselect" style={{ display: "grid", gap: "0.55rem", marginBottom: "0.5rem" }}>
              {TRADES.map((g) => (
                <div key={g.group} style={{ background: `${CYAN}0d`, border: `1px solid ${CYAN}44`, borderRadius: 6, padding: "0.7rem 0.85rem" }}>
                  <div style={{ color: CYAN, fontFamily: MONO, fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 8 }}>
                    {g.group}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.4rem 1rem" }}>
                    {g.items.map((t) => {
                      const checked = form.trades.includes(t.v);
                      return (
                        <label key={t.v} style={{ display: "flex", alignItems: "flex-start", gap: 6, color: WHITE, fontSize: "0.88rem", cursor: "pointer", lineHeight: 1.35 }}>
                          <input
                            type="checkbox"
                            data-testid={`trade-${t.v}`}
                            checked={checked}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...form.trades, t.v]
                                : form.trades.filter((x) => x !== t.v);
                              set("trades", next);
                            }}
                            style={{ marginTop: 2, accentColor: CYAN }}
                          />
                          <span>{t.l}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            {form.trades.length > 0 && (
              <div data-testid="trades-selected-count" style={{ color: GREEN, fontFamily: MONO, fontSize: "0.82rem", fontWeight: 700, letterSpacing: "0.08em", marginTop: "0.4rem" }}>
                ✓ {form.trades.length} TRADE{form.trades.length > 1 ? "S" : ""} SELECTED
              </div>
            )}
          </Section>
          )}
          </>}

          {step === 2 && <>
          <Section title={isIndividual ? "About You (helps us prioritize small operators)" : "Your Size (helps us prioritize small operators)"}>
            <label style={labelStyle}>{isIndividual ? "How long have you been doing this kind of work?" : "How long has your business been running?"}</label>
            <select data-testid="business_age_years" value={form.business_age_years} onChange={(e) => set("business_age_years", e.target.value)} style={inputStyle}>
              <option value="">-- Select --</option>
              <option value="0-3">Less than 3 years</option>
              <option value="3-7">3 to 7 years</option>
              <option value="7+">7+ years</option>
            </select>
            <label style={labelStyle}>How many people work with you?</label>
            <select data-testid="employee_count" value={form.employee_count} onChange={(e) => set("employee_count", e.target.value)} style={inputStyle}>
              <option value="">-- Select --</option>
              <option value="1-5">Just me / 1-5 people</option>
              <option value="6-15">6-15 people</option>
              <option value="16+">16+ people</option>
            </select>
            {!isIndividual && <>
              <Yesno label="Do you personally work on-site (owner-operator)?" testid="owner_is_operator" value={form.owner_is_operator} onChange={(v) => set("owner_is_operator", v)} />
              <Yesno label="Is your business a franchise location?" testid="is_franchise" value={form.is_franchise} onChange={(v) => set("is_franchise", v)} />
              <Yesno label="Does your business have a public-company parent?" testid="has_public_parent" value={form.has_public_parent} onChange={(v) => set("has_public_parent", v)} />
              <Yesno label="Is your trade license held in your personal name (not a corporate umbrella)?" testid="license_held_individually" value={form.license_held_individually} onChange={(v) => set("license_held_individually", v)} />
            </>}
            {isIndividual && (
              <Yesno label="Do you personally do the work on-site?" testid="owner_is_operator" value={form.owner_is_operator} onChange={(v) => set("owner_is_operator", v)} />
            )}
          </Section>
          </>}

          {step === 3 && <>
          <Section title="Safety Training (optional · adds points to your application)">
            <div style={{ background: `${CYAN}10`, border: `1px solid ${CYAN}55`, borderRadius: 6, padding: "0.85rem 0.95rem", fontSize: "0.86rem", color: WHITE, lineHeight: 1.6, marginBottom: "0.6rem" }}>
              <div style={{ color: CYAN, fontFamily: MONO, fontSize: "0.66rem", letterSpacing: "0.2em", fontWeight: 800, marginBottom: 6 }}>
                &gt; WHAT IS OSHA 30?
              </div>
              <div style={{ marginBottom: 8 }}>
                It&rsquo;s a one-time <strong style={{ color: WHITE }}>30-hour safety class</strong> that teaches you how to work safely on a job site &mdash; ladders, chemicals, tools, lifting, electrical hazards, all of it. When you finish, you get a <strong style={{ color: WHITE }}>wallet card</strong> with your name on it.
              </div>
              <div style={{ marginBottom: 8 }}>
                Big property owners (apartment portfolios like BH Management) <strong style={{ color: WHITE }}>require this card</strong> before they let anyone work on their buildings. So if you have it, we can send you their jobs. If not, no worries &mdash; you can still work Tier B (referral) jobs.
              </div>
              <div style={{ color: CYAN }}>
                <strong>Have it? Check the box &mdash; +10 points on your application, moves you toward auto-approve.</strong>
              </div>
              <div style={{ marginTop: 8, color: SILVER, fontSize: "0.8rem" }}>
                Don&rsquo;t have it yet? Take the online class ($150-$180) at <span style={{ color: WHITE, fontFamily: MONO }}>osha.com</span> or <span style={{ color: WHITE, fontFamily: MONO }}>360training.com</span>. About a week to finish.
              </div>
            </div>
            <Checkbox
              label={isIndividual
                ? "Yes — I've completed the OSHA 30-Hour Construction Safety class and can show my wallet card if asked."
                : "Yes — I (or my crew lead) have completed the OSHA 30-Hour Construction Safety class and can show a wallet card if asked."}
              testid="osha_30_attested"
              value={form.osha_30_attested}
              onChange={(v) => set("osha_30_attested", v)}
            />
          </Section>

          <Section title="Anything else?">
            <Field label="Who referred you? (optional)" testid="referred_by" value={form.referred_by} onChange={(v) => set("referred_by", v)} />
            <label style={labelStyle}>Notes (optional)</label>
            <textarea data-testid="notes" value={form.notes} onChange={(e) => set("notes", e.target.value)}
                      rows={3} style={{ ...inputStyle, resize: "vertical" }} />
          </Section>

          {/* ─── Contractor Terms & Disclaimer — REQUIRED ─── */}
          <Section title="Contractor Terms & Disclaimer (required)">
            <div data-testid="disclaimer-box" style={{
              background: "rgba(2,10,31,0.9)", border: `1px solid ${CYAN}55`,
              borderRadius: 6, padding: "0.95rem 1rem",
              maxHeight: 280, overflowY: "auto",
              color: WHITE, fontSize: "0.82rem", lineHeight: 1.6,
              boxShadow: `inset 0 0 20px rgba(0,0,0,0.4)`,
            }}>
              <div style={{ color: CYAN, fontFamily: MONO, fontSize: "0.66rem", letterSpacing: "0.22em", fontWeight: 800, marginBottom: 10 }}>
                &gt; PLEASE READ BEFORE SUBMITTING · {DISCLAIMER_VERSION}
              </div>

              <p style={{ margin: "0 0 0.7rem" }}>
                <strong style={{ color: CYAN }}>1. Independent Contractor Status.</strong>{" "}
                You are applying as an <strong>independent 1099 contractor</strong>, not an employee of Moovin Forward LLC. Nothing in this application creates an employment, agency, partnership, or joint-venture relationship. You control the manner and means of your own work.
              </p>

              <p style={{ margin: "0 0 0.7rem" }}>
                <strong style={{ color: CYAN }}>2. No Guarantee of Work.</strong>{" "}
                Submitting this application does not entitle you to any specific job, minimum volume, or ongoing engagement. Work opportunities are offered case-by-case based on trade fit, location, availability, and owner requirements.
              </p>

              <p style={{ margin: "0 0 0.7rem" }}>
                <strong style={{ color: CYAN }}>3. Your Responsibilities.</strong>{" "}
                As a 1099 contractor, <strong>you are solely responsible</strong> for:
              </p>
              <ul style={{ margin: "0 0 0.7rem", paddingLeft: "1.2rem" }}>
                <li>Your own federal, state, and self-employment taxes</li>
                <li>Your own general liability insurance and workers&rsquo; comp (where required)</li>
                <li>Any trade license, permit, or certification your scope requires</li>
                <li>Your own tools, materials, transportation, and workspace</li>
                <li>Compliance with OSHA, EPA (RRP if applicable), ADA, fair-housing, and all federal/state/local laws</li>
                <li>The quality and completion of any work you accept</li>
              </ul>

              <p style={{ margin: "0 0 0.7rem" }}>
                <strong style={{ color: CYAN }}>4. Moovin Forward Is a Network, Not a Broker or PM.</strong>{" "}
                Moovin Forward LLC does <strong>not</strong> provide real-estate brokerage, property management, or leasing services. On Tier B (referral) jobs, you contract <strong>directly with the property owner</strong>. On Tier A (direct-crew) jobs, Moovin Forward may pay you as a 1099 contractor for scoped work only.
              </p>

              <p style={{ margin: "0 0 0.7rem" }}>
                <strong style={{ color: CYAN }}>5. Information Sharing.</strong>{" "}
                Your application details may be shared with property owners, receivers, asset managers, or investor partners for the purpose of matching you to jobs. You represent that all information you provide is <strong>truthful and accurate</strong>. Providing false info is grounds for immediate removal from the network.
              </p>

              <p style={{ margin: "0 0 0.7rem" }}>
                <strong style={{ color: CYAN }}>6. Right to Decline.</strong>{" "}
                Moovin Forward LLC reserves the right to decline any application, remove any contractor, or cancel any job assignment at its sole discretion, with or without cause.
              </p>

              <p style={{ margin: "0 0 0.7rem" }}>
                <strong style={{ color: CYAN }}>7. Background & References.</strong>{" "}
                You consent to Moovin Forward LLC verifying your license, insurance, references, and (where legally permitted) conducting a background check before assigning on-site work at institutional properties.
              </p>

              <p style={{ margin: "0 0 0.7rem" }}>
                <strong style={{ color: CYAN }}>8. Confidentiality &amp; Non-Circumvention.</strong>{" "}
                Property addresses, owner contacts, scope pricing, and internal procedures shared with you are confidential trade information (protected under TUTSA + DTSA). You agree not to bypass Moovin Forward to solicit the same owner directly for the same scope within <strong>12 months</strong> of introduction.
              </p>

              <p style={{ margin: "0 0 0.7rem" }}>
                <strong style={{ color: CYAN }}>9. Indemnification.</strong>{" "}
                You agree to indemnify and hold harmless Moovin Forward LLC, its officers, and its property-owner clients from any claim, loss, injury, or damage arising out of your work, your conduct, your subcontractors, your equipment, or your failure to comply with applicable laws.
              </p>

              <p style={{ margin: "0 0 0.7rem" }}>
                <strong style={{ color: CYAN }}>10. Governing Law.</strong>{" "}
                This acknowledgement is governed by the laws of the State of Texas. Any dispute is subject to venue in Tarrant County, Texas.
              </p>

              <p style={{ margin: "0.5rem 0 0", color: SILVER, fontSize: "0.78rem" }}>
                Consent to receive SMS/email about network opportunities is granted by submitting. Reply STOP to opt out of texts. Full contact: <span style={{ color: WHITE, fontFamily: MONO }}>hello@moovinforward.org</span>.
              </p>
            </div>

            <label data-testid="disclaimer-checkbox-wrap" style={{
              display: "flex", alignItems: "flex-start", gap: "0.6rem", marginTop: "0.9rem",
              color: WHITE, fontSize: "0.92rem", cursor: "pointer", lineHeight: 1.5,
              padding: "0.7rem 0.85rem",
              background: form.disclaimer_accepted ? `${CYAN}15` : "rgba(255,138,138,0.05)",
              border: `1px solid ${form.disclaimer_accepted ? CYAN : SILVER + "44"}`,
              borderRadius: 6,
              boxShadow: form.disclaimer_accepted ? `0 0 12px ${CYAN}44` : "none",
              transition: "all 0.15s",
            }}>
              <input type="checkbox" data-testid="disclaimer_accepted"
                     checked={form.disclaimer_accepted}
                     onChange={(e) => set("disclaimer_accepted", e.target.checked)}
                     style={{ width: 20, height: 20, marginTop: 2, accentColor: CYAN, flexShrink: 0 }} />
              <span>
                <strong style={{ color: form.disclaimer_accepted ? CYAN : WHITE }}>
                  I have read the Contractor Terms &amp; Disclaimer above
                </strong>
                {" "}and I agree to them. I confirm the information I&rsquo;ve provided is truthful, and I understand I&rsquo;m applying as an <strong>independent 1099 contractor</strong> — not an employee of Moovin Forward LLC.
              </span>
            </label>
          </Section>
          </>}

          {/* Wizard navigation */}
          <div style={{ display: "flex", gap: 8, marginTop: "1rem", flexWrap: "wrap" }}>
            {step > 1 && (
              <button type="button" data-testid="wizard-back"
                      onClick={() => setStep(step - 1)}
                      style={{ background: "transparent", color: CYAN, border: `1px solid ${CYAN}`,
                               padding: "0.8rem 1.2rem", borderRadius: 5, fontSize: "0.82rem", fontFamily: MONO,
                               fontWeight: 700, letterSpacing: "0.1em", cursor: "pointer" }}>
                ← BACK
              </button>
            )}
            {step < 3 && (
              <button type="button" data-testid="wizard-next"
                      onClick={() => canAdvance() ? setStep(step + 1) : alert("Please pick worker type, fill in your name, email, and select at least one trade.")}
                      style={{ background: canAdvance() ? CYAN : `${SILVER}55`,
                               color: canAdvance() ? BG : SILVER, padding: "0.85rem 1.2rem", borderRadius: 5,
                               border: "none", fontSize: "0.82rem", fontFamily: MONO, fontWeight: 800,
                               letterSpacing: "0.14em", cursor: canAdvance() ? "pointer" : "not-allowed", flex: 1 }}>
                NEXT STEP →
              </button>
            )}
            {step === 3 && (
              <button type="submit" data-testid="submit-application" disabled={submitting || !form.disclaimer_accepted}
                      style={{ background: (!form.disclaimer_accepted || submitting) ? `${SILVER}55` : CYAN,
                               color: (!form.disclaimer_accepted || submitting) ? SILVER : BG,
                               padding: "0.85rem 1.2rem", borderRadius: 5, border: "none",
                               fontSize: "0.82rem", fontFamily: MONO, fontWeight: 800, letterSpacing: "0.14em",
                               cursor: (submitting || !form.disclaimer_accepted) ? "not-allowed" : "pointer", flex: 1,
                               opacity: (submitting || !form.disclaimer_accepted) ? 0.7 : 1 }}>
                {submitting ? "SUBMITTING…" : !form.disclaimer_accepted ? "ACKNOWLEDGE DISCLAIMER TO SUBMIT" : "SUBMIT APPLICATION >"}
              </button>
            )}
          </div>

          {/* Save & finish later */}
          <div style={{ textAlign: "center", marginTop: "0.9rem" }}>
            <button type="button" data-testid="save-finish-later"
                    onClick={saveAndFinishLater}
                    style={{ background: "transparent", border: "none", color: SILVER, fontFamily: MONO,
                             fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.14em", cursor: "pointer",
                             textDecoration: "underline" }}>
              SAVE DRAFT &amp; FINISH LATER
            </button>
            {savedNotice && (
              <div data-testid="save-notice" style={{ marginTop: 6, color: GREEN, fontFamily: MONO, fontSize: "0.72rem", fontWeight: 600, letterSpacing: "0.1em" }}>
                ✓ SAVED · COME BACK ON THIS DEVICE TO CONTINUE
              </div>
            )}
          </div>

          <p style={{ color: MUTED, fontSize: "0.72rem", textAlign: "center", marginTop: "0.9rem", lineHeight: 1.55, fontFamily: MONO, letterSpacing: "0.04em" }}>
            By submitting, you consent to Moovin Forward LLC contacting you about network opportunities. Your info is protected under TUTSA + DTSA.<br />
            <strong style={{ color: SILVER }}>NO BROKERAGE · NO PROPERTY MANAGEMENT · NO LEASING</strong>
          </p>
        </form>
      </div>
    </div>
  );
};

// ─── Presentational helpers ─────────────────────────────────────
const pageStyle = {
  minHeight: "100vh", background: BG, color: WHITE, fontFamily: SANS,
  backgroundImage: `radial-gradient(900px 500px at 50% 0%, ${CYAN}12 0%, transparent 60%), linear-gradient(180deg, ${BG} 0%, #030F2A 100%)`,
};
const labelStyle = {
  display: "block", color: CYAN, fontFamily: MONO, fontSize: "0.7rem",
  fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase",
  marginTop: "0.9rem", marginBottom: "0.35rem",
};
const inputStyle = {
  width: "100%", padding: "0.7rem 0.8rem",
  border: `1px solid ${SILVER}55`, borderRadius: 4,
  background: "rgba(2,10,31,0.7)", color: WHITE, fontFamily: MONO, fontSize: "0.9rem",
  boxSizing: "border-box", outline: "none",
  transition: "border-color 0.15s, box-shadow 0.2s",
};

const Section = ({ title, children }) => (
  <div style={{ marginBottom: "1.4rem", paddingBottom: "1.2rem", borderBottom: `1px solid ${SILVER}22` }}>
    <div style={{ color: CYAN, fontFamily: MONO, fontSize: "0.7rem", letterSpacing: "0.22em",
                  textTransform: "uppercase", fontWeight: 800, marginBottom: "0.5rem" }}>
      &gt; {title}
    </div>
    {children}
  </div>
);

const Field = ({ label, testid, value, onChange, type = "text", placeholder = "" }) => (
  <div>
    <label style={labelStyle}>{label}</label>
    <input type={type} data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
           placeholder={placeholder} style={inputStyle}
           onFocus={(e) => { e.target.style.borderColor = CYAN; e.target.style.boxShadow = `0 0 12px ${CYAN}44`; }}
           onBlur={(e) => { e.target.style.borderColor = `${SILVER}55`; e.target.style.boxShadow = "none"; }} />
  </div>
);

const Row = ({ children }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.7rem" }}>
    {children}
  </div>
);

const Yesno = ({ label, testid, value, onChange }) => (
  <div style={{ marginTop: "0.8rem" }}>
    <div style={{ color: WHITE, fontSize: "0.9rem", fontWeight: 500, marginBottom: "0.35rem", lineHeight: 1.45 }}>{label}</div>
    <div style={{ display: "flex", gap: "0.5rem" }}>
      {[{ v: true, l: "Yes" }, { v: false, l: "No" }].map((o) => (
        <button key={o.l} type="button" data-testid={`${testid}-${o.l.toLowerCase()}`}
                onClick={() => onChange(o.v)}
                style={{ padding: "0.5rem 1.1rem", borderRadius: 4,
                         border: `1px solid ${value === o.v ? CYAN : SILVER + "55"}`,
                         background: value === o.v ? `${CYAN}20` : "transparent",
                         color: value === o.v ? CYAN : WHITE,
                         fontFamily: MONO, fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.1em",
                         cursor: "pointer", boxShadow: value === o.v ? `0 0 10px ${CYAN}44` : "none" }}>
          {o.l.toUpperCase()}
        </button>
      ))}
    </div>
  </div>
);

const Checkbox = ({ label, testid, value, onChange }) => (
  <label style={{ display: "flex", alignItems: "flex-start", gap: "0.6rem", marginTop: "0.5rem",
                  color: WHITE, fontSize: "0.9rem", cursor: "pointer", lineHeight: 1.5 }}>
    <input type="checkbox" data-testid={testid} checked={value} onChange={(e) => onChange(e.target.checked)}
           style={{ width: 18, height: 18, marginTop: 2, accentColor: CYAN }} />
    <span>{label}</span>
  </label>
);

const WorkerTypeButton = ({ testid, active, onClick, label, hint }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    style={{
      textAlign: "left", cursor: "pointer",
      background: active ? `${CYAN}18` : "rgba(2,10,31,0.7)",
      border: `1px solid ${active ? CYAN : SILVER + "44"}`,
      color: active ? CYAN : WHITE,
      borderRadius: 6, padding: "0.85rem 0.95rem",
      boxShadow: active ? `0 0 16px ${CYAN}55` : "none",
      transition: "border-color 0.15s, box-shadow 0.2s, background 0.15s",
    }}>
    <div style={{ fontFamily: MONO, fontSize: "0.62rem", letterSpacing: "0.14em", color: active ? CYAN : SILVER, marginBottom: 4, fontWeight: 700 }}>
      {active ? "SELECTED" : "SELECT"}
    </div>
    <div style={{ fontSize: "0.95rem", fontWeight: 700, lineHeight: 1.2, marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: "0.75rem", color: SILVER, lineHeight: 1.4 }}>{hint}</div>
  </button>
);

export default VendorApplication;
